import { BrDivider } from '@govbr-ds/react-components';
import BaseDeCalculoAccordion from 'components/baseDeCalculoAccordion/baseDeCalculoAccordion';
import { getReceitaLabel } from 'enums/EBaseCalculo';
import { useNotificacao } from 'hooks/useNotificacao';
import { PaginasOutrosRegimes } from 'interfaces/IBasesCalculo';
import { IOutrasBasesNcmReceber } from 'interfaces/IOutrasBases';
import { useBackEndRequests } from 'pages/TitularArrendatario/lib/backEndRequests';
import { Col, Row, Table } from 'react-dsgov';
import { formatCurrency } from 'utils/formatCurrency';
import { formatPercentual } from 'utils/formatPercentual';
import styles from './ncmAccordion.module.scss';

interface INcmAccordionProps {
  isEditable: boolean;
  ncm: IOutrasBasesNcmReceber;
  editFunction?: (ncm: IOutrasBasesNcmReceber, id: number) => void;
  refetchNCMList?: () => void;
  tipoBaseCalculo?: PaginasOutrosRegimes;
}

const NcmAccordion = ({ isEditable, ncm, editFunction, refetchNCMList, tipoBaseCalculo }: INcmAccordionProps) => {
  const { deleteNCMOutrasBasesCalculo } = useBackEndRequests();

  const mostrarNotificacao = useNotificacao();

  const deleteNCM = async () => {
    await deleteNCMOutrasBasesCalculo(ncm.id)
      .then(() => {
        mostrarNotificacao({
          tipo: 'success',
          conteudo: 'NCM excluído com sucesso',
        });
        if (refetchNCMList) {
          refetchNCMList();
        }
      })
      .catch((err) => {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Erro ao excluir a guia de recolhimento, tente novamente mais tarde. Erro: ' + err.message,
        });
      });
  };

  return (
    <div>
      <BaseDeCalculoAccordion
        title={'NCM - ' + ncm?.codigoNcm}
        deleteFunction={() => deleteNCM()}
        isNCM={true}
        isEditable={isEditable}
        editFunction={editFunction ? () => editFunction(ncm, ncm.id) : () => {}}
      >
        <Row>
          <Col className={styles.dadosCol}>
            <span className={styles.label}>Código NCM</span>
            <span className={styles.value}>{ncm?.codigoNcm}</span>
          </Col>
          <Col className={styles.dadosCol}>
            <span className={styles.label}>
              {tipoBaseCalculo ? getReceitaLabel(tipoBaseCalculo) : 'Receita Bruta (R$)'}
            </span>
            <span className={styles.value}>{formatCurrency(ncm?.valorReceitaBruta)}</span>
          </Col>
          <Col className={styles.dadosCol}>
            <span className={styles.label}>Unidade de Medida</span>
            <span className={styles.value}>{ncm?.unidadeMedida}</span>
          </Col>
        </Row>
        <Row>
          <Col className={styles.dadosCol}>
            <span className={styles.label}>Quantidade</span>
            <span className={styles.value}>{ncm?.quantidade}</span>
          </Col>
          <Col className={styles.dadosCol}>
            <span className={styles.label}>Preço Médio</span>
            <span className={styles.value}>{formatCurrency(ncm?.valorPrecoMedio)}</span>
          </Col>
          <Col></Col>
        </Row>

        <div className={styles.divider}>
          <BrDivider />
        </div>
        <div className={styles.sectionTitle}>Deduções</div>
        {ncm?.tributosIncidentes && ncm?.tributosIncidentes.length > 0 ? (
          <>
            {ncm?.tributosIncidentes.map((deducao, index) => (
              <Row key={index} className={styles.deducoesRow}>
                <Col>
                  <span className={styles.label}>{deducao.nome}</span>
                </Col>
                <Col>
                  <span className={styles.text}>{formatCurrency(deducao.valor ?? 0)}</span>
                </Col>
                <Col></Col>
              </Row>
            ))}
            <Row className={styles.total}>
              <Col>Total:</Col>
              <Col>{formatCurrency(ncm?.valorTotalTributos)}</Col>
              <Col></Col>
            </Row>
          </>
        ) : (
          <span className={styles.noDeductions}>Nenhuma dedução cadastrada.</span>
        )}
        <div className={styles.divider}>
          <BrDivider />
        </div>
        <div className={styles.sectionTitle}>Modalidades de Afetação</div>
        {ncm?.modalidadesAfetacaoFerrovia && ncm.modalidadesAfetacaoFerrovia.length > 0 && (
          <div className={styles.tableContainer}>
            <div className={styles.modalidadeTitle}>Ferrovia</div>
            <Table showTopBar={false} showPageSelector={false} showPagination={false} density='small'>
              <thead>
                <tr>
                  <th>Ferrovia</th>
                  <th>Cidade/UF Origem</th>
                  <th>Cidade/UF Destino</th>
                </tr>
              </thead>
              <tbody>
                {ncm.modalidadesAfetacaoFerrovia.map((ferrovia, index) => (
                  <tr key={index}>
                    <td>{ferrovia?.ferrovia?.nome + ' - ' + ferrovia?.ferrovia?.sigla}</td>
                    <td>{ferrovia?.municipioOrigem?.nome + '/' + ferrovia?.municipioOrigem?.uf}</td>
                    <td>{ferrovia?.municipioDestino?.nome + '/' + ferrovia?.municipioDestino?.uf}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        {ncm?.possuiAfetacaoDutovia && (
          <div className={styles.tableContainer}>
            <div className={styles.modalidadeTitle}>Dutovia</div>
            <span className={styles.value}>Possui afetação por dutovia</span>
          </div>
        )}
        {ncm?.modalidadesAfetacaoPorto && ncm.modalidadesAfetacaoPorto.length > 0 && (
          <div className={styles.tableContainer}>
            <div className={styles.modalidadeTitle}>Operações Portuárias</div>
            <Table showTopBar={false} showPageSelector={false} showPagination={false} density='small'>
              <thead>
                <tr>
                  <th>Município/UF do Porto</th>
                </tr>
              </thead>
              <tbody>
                {ncm.modalidadesAfetacaoPorto.map((operacao, index) => (
                  <tr key={index}>
                    <td>{operacao?.porto.cidade + '/' + operacao?.porto.uf}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        {ncm?.modalidadesAfetacaoMunicipioMineracao && ncm.modalidadesAfetacaoMunicipioMineracao.length > 0 && (
          <div className={styles.tableContainer}>
            <div className={styles.modalidadeTitle}>Estruturas fora do município produtor</div>
            <Table showTopBar={false} showPageSelector={false} showPagination={false} density='small'>
              <thead>
                <tr>
                  <th>UF da Estrutura</th>
                  <th>Município da Estrutura</th>
                </tr>
              </thead>
              <tbody>
                {ncm.modalidadesAfetacaoMunicipioMineracao.map((estrutura, index) => (
                  <tr key={index}>
                    <td>{estrutura?.municipioMineracao?.uf}</td>
                    <td>{estrutura?.municipioMineracao?.nome}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        {ncm?.modalidadesAfetacaoFerrovia.length === 0 &&
          ncm?.possuiAfetacaoDutovia === false &&
          ncm?.modalidadesAfetacaoPorto.length === 0 &&
          ncm?.modalidadesAfetacaoMunicipioMineracao.length === 0 && (
            <span className={styles.noModalities}>Nenhuma modalidade de afetação cadastrada.</span>
          )}
        <div className={styles.divider}>
          <BrDivider />
        </div>
        <Row className={styles.bottomRow}>
          <Col className={styles.dadosCol}>
            <span className={styles.label}>Rejeito ou Estéreis</span>
            <span className={styles.value}>{ncm?.isRejeito ? 'Sim' : 'Não'}</span>
          </Col>
          <Col className={styles.dadosCol}>
            <span className={styles.label}>Uso Específico</span>
            <span className={styles.value}>{ncm?.hasUsoEspecifico ? 'Sim' : 'Não'}</span>
          </Col>
          <Col className={styles.dadosCol}>
            <span className={styles.label}>Tipo de Uso</span>
            <span className={styles.value}>{ncm?.tipoDeUso?.nome}</span>
          </Col>
        </Row>
        <Row>
          <Col className={styles.dadosCol}>
            <span className={styles.label}>Base de Cálculo (R$)</span>
            <span className={styles.value}>{formatCurrency(ncm?.valorBaseCalculo)}</span>
          </Col>
          <Col className={styles.dadosCol}>
            <span className={styles.label}>Tipo de Alíquota</span>
            <span className={styles.value}>{ncm?.tipoAliquota}</span>
          </Col>
          <Col className={styles.dadosCol}>
            <span className={styles.label}>Alíquota</span>
            <span className={styles.value}>{formatPercentual(ncm?.valorAliquota)}</span>
          </Col>
        </Row>
        <Row>
          <Col className={styles.dadosCol}>
            <span className={styles.label}>CFEM Total a Recolher (R$)</span>
            <span className={styles.value}>{formatCurrency(ncm?.valorCfem)}</span>
          </Col>
          <Col></Col>
          <Col></Col>
        </Row>
      </BaseDeCalculoAccordion>
    </div>
  );
};

export default NcmAccordion;
